import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Clock,
  Ticket,
  FileText,
  CreditCard,
  FileCheck,
  FileSignature,
  PenLine,
  Monitor,
  FolderKanban,
  User,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Users,
  Shield,
  Settings,
  Plug,
  Activity,
  Building2,
  Mail,
  Key,
  KeySquare,
  Moon,
  Sun,
  BookOpen,
  Server,
  Calendar,
  Box,
  Layers,
  Search,
  Wallet, LifeBuoy, Globe, Lock} from 'lucide-react';
import { useAuthStore } from '../../context/authStore';
import { useEntitlements } from '../../context/featureFlagStore';
import { notInPlanMessage } from './NotInPlan';
import { useThemeStore } from '../../context/themeStore';
import { useFeatureFlag, useFeatureFlags } from '../../context/featureFlagStore';
import { useBranding } from '../../hooks/useBranding';
import NotificationDropdown from './NotificationDropdown';
import SandboxBanner from './SandboxBanner';
import api, { brandingAPI } from '../../services/api';
import { useBrandingStore } from '../../context/brandingStore';
import { applyCompanyBranding, resetCompanyBranding, shellBranding } from '../../utils/companyBranding';

/**
 * Main navigation items
 * minRole: minimum role required to see this item (uses hierarchy)
 * flag: optional feature flag key that must be enabled
 */
const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Tickets', href: '/tickets', icon: Ticket, perm: ['tickets', 'read'] },
  { name: 'Invoices', href: '/invoices', icon: FileText, perm: ['invoices', 'read'] },
  { name: 'Payments', href: '/payments', icon: CreditCard, flag: 'module.payments', perm: ['payments', 'read'] },
  { name: 'Quotes', href: '/quotes', icon: FileCheck, flag: 'module.quotes', perm: ['quotes', 'read'] },
  { name: 'Agreements', href: '/agreements', icon: FileSignature, flag: 'module.agreements', perm: ['agreements', 'read'] },
  { name: 'Contracts', href: '/contracts', icon: PenLine, flag: 'module.contracts', perm: ['contracts', 'read'] },
  { name: 'Assets', href: '/assets', icon: Monitor, flag: 'module.configurations', perm: ['assets', 'read'] },
  { name: 'Documentation', href: '/documentation', icon: BookOpen, flag: 'module.documentation', perm: ['documentation', 'read'] },
  { name: 'Vault', href: '/vault', icon: KeySquare, flag: 'module.vault', perm: ['vault', 'read'] },
  { name: 'Projects', href: '/projects', icon: FolderKanban, flag: 'module.projects', perm: ['projects', 'read'] },
  { name: 'Team', href: '/team', icon: Users, flag: 'module.team', perm: ['company_users', 'manage'] },
];

/**
 * Admin navigation items (internal staff only)
 * adminPerm: required admin permission from role's permissions.admin array
 */
const adminNavigation = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, minRole: 'technician', adminPerm: 'dashboard' },
  { name: 'Global Search', href: '/documentation/search', icon: Search, minRole: 'technician', flag: 'module.documentation' },
  { name: 'Documentation', href: '/documentation', icon: BookOpen, minRole: 'technician', flag: 'module.documentation' },
  { name: 'Configurations', href: '/documentation/configurations', icon: Server, minRole: 'technician', flag: 'module.documentation' },
  { name: 'Flexible Assets', href: '/documentation/flexible-assets', icon: Box, minRole: 'technician', flag: 'module.documentation' },
  { name: 'Expirations', href: '/documentation/expirations', icon: Calendar, minRole: 'technician', flag: 'module.documentation' },
  { name: 'Activity', href: '/documentation/activity', icon: Activity, minRole: 'technician', flag: 'module.documentation' },
  { name: 'Vault', href: '/vault', icon: KeySquare, minRole: 'technician', flag: 'module.vault' },
  { name: 'Quotes', href: '/admin/quotes', icon: FileCheck, minRole: 'technician', adminPerm: 'quotes' },
  { name: 'Invoices', href: '/admin/invoices', icon: FileText, minRole: 'technician', adminPerm: 'invoices' },
  { name: 'Client Payments', href: '/admin/client-payments', icon: Wallet, minRole: 'technician', adminPerm: 'invoices' },
  { name: 'Agreements', href: '/admin/agreements', icon: FileSignature, minRole: 'technician', adminPerm: 'agreements' },
  { name: 'Tickets', href: '/admin/tickets', icon: Ticket, minRole: 'technician', adminPerm: 'tickets' },
  { name: 'User Management', href: '/admin/users', icon: Users, minRole: 'admin', adminPerm: 'users' },
  { name: 'Companies', href: '/admin/companies', icon: Building2, minRole: 'technician', adminPerm: 'companies' },
  { name: 'Sessions', href: '/admin/sessions', icon: Activity, minRole: 'admin', adminPerm: 'sessions' },
  { name: 'Audit Log', href: '/admin/audit', icon: Shield, minRole: 'technician', adminPerm: 'audit' },
  { name: 'Email Templates', href: '/admin/email-templates', icon: Mail, minRole: 'admin', adminPerm: 'email_templates' },
  { name: 'API Keys', href: '/admin/api-keys', icon: Key, minRole: 'admin', adminPerm: 'api_keys' },
  { name: 'Feature Flags', href: '/admin/feature-flags', icon: Settings, minRole: 'admin', adminPerm: 'feature_flags' },
  { name: 'Asset Types', href: '/admin/asset-types', icon: Layers, minRole: 'admin', adminPerm: 'asset_types' },
  { name: 'Roles', href: '/admin/roles', icon: Shield, minRole: 'admin', adminPerm: 'roles' },
  { name: 'Portal Settings', href: '/admin/portal-settings', icon: Settings, minRole: 'admin', adminPerm: 'settings' },
  { name: 'Domains', href: '/admin/domains', icon: Globe, minRole: 'admin', adminPerm: 'settings' },
  // No flag: the page holds ConnectWise, payments, email delivery and SSO as
  // well as Ticket Alerts, so a tenant with that flag off (the default) still
  // needs it. Found on a fresh staging tenant with no way to reach it.
  { name: 'Integrations', href: '/admin/integrations', icon: Plug, minRole: 'admin', adminPerm: 'integrations' },
  // `module`: an add-on with no flag of its own. Locked rather than hidden
  // when the plan lacks it, like a flagged item whose flag is locked.
  { name: 'Time', href: '/admin/time', icon: Clock, minRole: 'technician', adminPerm: 'time_tracking', module: 'time' },
  { name: 'Contracts', href: '/admin/contracts', icon: PenLine, minRole: 'technician', adminPerm: 'contracts', module: 'contracts' },
];

/**
 * Role badge colors and labels
 */
const roleBadges = {
  admin: { label: 'Admin', className: 'bg-red-100 text-red-700' },
  technician: { label: 'Technician', className: 'bg-purple-100 text-purple-700' },
  finance: { label: 'Finance', className: 'bg-green-100 text-green-700' },
  company_admin: { label: 'Company Admin', className: 'bg-blue-100 text-blue-700' },
  user: { label: 'User', className: 'bg-neutral-100 text-neutral-700' },
};

/** The operator's notice for this portal, read once on load. */
const BANNER_STYLES = {
  info: 'bg-blue-50 text-blue-900 border-blue-200 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-800',
  warning: 'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-900/40 dark:text-amber-100 dark:border-amber-800',
  critical: 'bg-red-50 text-red-900 border-red-200 dark:bg-red-900/40 dark:text-red-100 dark:border-red-800',
};

const DISMISSED_KEY = 'platform-banners-dismissed';

function readDismissed() {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '[]');
  } catch {
    return [];
  }
}

function PlatformBanners() {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(() => readDismissed());

  useEffect(() => {
    let cancelled = false;
    // Public, resolved from the hostname the way the branding was; never
    // worth an error if it cannot be reached.
    api
      .get('/tenant/banner', { params: { host: window.location.hostname } })
      .then((res) => {
        if (!cancelled && Array.isArray(res.data?.banners)) setBanners(res.data.banners);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = banners.filter((b) => !dismissed.includes(b.id));
  if (!visible.length) return null;

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try {
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch {
      /* a private window with storage blocked still gets to dismiss for this render */
    }
  };

  return (
    <div>
      {visible.map((b) => (
        <div
          key={b.id}
          role="status"
          className={`flex items-start justify-between gap-4 border-b px-4 py-2 text-sm sm:px-6 ${BANNER_STYLES[b.level] || BANNER_STYLES.info}`}
        >
          <span>{b.message}</span>
          <button type="button" onClick={() => dismiss(b.id)} className="flex-shrink-0 opacity-70 hover:opacity-100" aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout, hasMinRole, can } = useAuthStore();
  const branding = useBranding();
  const { isDarkMode, toggleDarkMode, initTheme } = useThemeStore();
  const darkModeEnabled = useFeatureFlag('ui.dark_mode');
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're viewing a quote from admin section (e.g., CW opportunity)
  const isFromAdmin = new URLSearchParams(location.search).get('from') === 'admin';

  // Initialize theme on mount
  useEffect(() => {
    initTheme();
  }, [initTheme]);

  // Client-level branding: once the user is known, their primary company may
  // have its own name, logo or colour laid over the MSP's. The shell's
  // pre-login branding (window.__branding, if the 5c shell set it) is the
  // starting point, so a failed fetch leaves the MSP's look in place rather
  // than a blank one.
  const [companyBranding, setCompanyBranding] = useState(() => shellBranding());
  const brandingRevision = useBrandingStore((s) => s.revision);
  useEffect(() => {
    if (!user) {
      resetCompanyBranding();
      setCompanyBranding(shellBranding());
      return undefined;
    }
    let cancelled = false;
    brandingAPI.me({ fresh: brandingRevision > 0 })
      .then((res) => {
        if (cancelled || !res.data?.branding) return;
        setCompanyBranding(applyCompanyBranding(res.data.branding));
      })
      .catch(() => {
        // The MSP's look stays; branding is never worth an error banner.
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, brandingRevision]);

  // Check if user is internal staff (technician or admin)
  const isInternalStaff = user?.isInternalStaff || hasMinRole('technician');

  // Get feature flags for navigation filtering
  const moduleFlags = useFeatureFlags([
    'module.payments',
    'module.quotes',
    'module.agreements',
    'module.configurations',
    'module.documentation',
    'module.vault',
    'module.projects',
    'module.team',
    'module.ticket_alerts'
  ]);

  // Filter navigation based on role and feature flags
  // Technicians only see admin section, not customer section
  const showCustomerSection = !isInternalStaff || hasMinRole('admin');
  const visibleNav = showCustomerSection ? navigation.filter(item => {
    // Check role requirement
    if (item.minRole && !hasMinRole(item.minRole)) return false;
    // Check feature flag (if no flag, always show)
    if (item.flag && !moduleFlags[item.flag]) return false;
    // The customer's role (Company Admin, Billing, Team Member, End User or
    // a custom one): an End User sees no Invoices or Payments at all.
    if (item.perm && !can(...item.perm)) return false;
    return true;
  }) : [];

  // Get user's admin permissions array
  const adminPerms = user?.permissions?.admin || [];

  // What the plan includes (Phase 7a). An admin item for a module the plan
  // lacks is shown LOCKED rather than hidden, so the MSP can see what
  // exists; the route behind it says "Not included in your plan" too.
  const entitlements = useEntitlements();
  const isLockedItem = (item) =>
    (item.flag && entitlements.locked.includes(item.flag)) ||
    (item.module && !entitlements.modules.includes(item.module));

  // Filter admin navigation based on user role, feature flags, and admin permissions
  const visibleAdminNav = adminNavigation
    .filter(item => {
      // Check role requirement
      if (item.minRole && !hasMinRole(item.minRole)) return false;
      // A locked item stays visible; the flag only hides it when it is
      // simply switched off.
      if (item.flag && !moduleFlags[item.flag] && !isLockedItem(item)) return false;
      // Check admin permission (if no adminPerm, always show for internal staff)
      if (item.adminPerm && !adminPerms.includes(item.adminPerm)) return false;
      return true;
    })
    .map((item) => ({ ...item, locked: isLockedItem(item) }));
  const lockedTitle = notInPlanMessage(branding?.supportEmail);

  // Get role badge info
  const userRole = user?.role || 'end_user';
  const roleBadge = roleBadges[userRole] || roleBadges.user;
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-neutral-900 dark:bg-neutral-950 transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-neutral-800">
            {/* The client company's look when it has one (5d), otherwise the
                tenant's (5c): logo if set, else the portal name. */}
            {(companyBranding?.logoUrl || branding.companyLogoUrl) ? (
              <img
                src={companyBranding?.logoUrl || branding.companyLogoUrl}
                alt={companyBranding?.portalName || branding.portalName || 'Portal'}
                className="h-8 max-w-[160px] object-contain"
              />
            ) : (
              <span className="text-xl font-bold text-white truncate" title={companyBranding?.portalName || branding.portalName}>
                {companyBranding?.portalName || branding.portalName}
              </span>
            )}
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-neutral-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {visibleNav.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => {
                  // Don't highlight customer Quotes or Tickets when viewing from admin
                  const shouldBeActive = isActive && !((item.href === '/quotes' || item.href === '/tickets') && isFromAdmin);
                  return `
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                    ${shouldBeActive
                      ? 'bg-primary-500 text-white'
                      : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
                    }
                  `;
                }}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </NavLink>
            ))}

            {/* Admin Section - Only visible to internal staff */}
            {isInternalStaff && visibleAdminNav.length > 0 && (
              <>
                <div className="pt-4 mt-4 border-t border-neutral-800">
                  <p className="px-3 mb-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    Administration
                  </p>
                  {visibleAdminNav.map((item) => item.locked ? (
                    <span
                      key={item.name}
                      title={lockedTitle}
                      aria-disabled="true"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-neutral-600 cursor-not-allowed"
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="flex-1">{item.name}</span>
                      <Lock className="w-3.5 h-3.5" />
                    </span>
                  ) : (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      end={item.href === '/admin'}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => {
                        // Highlight admin Quotes/Tickets when viewing detail from admin
                        const isQuoteFromAdmin = item.href === '/admin/quotes' && isFromAdmin && location.pathname.startsWith('/quotes/');
                        const isTicketFromAdmin = item.href === '/admin/tickets' && isFromAdmin && location.pathname.startsWith('/tickets/');
                        const shouldBeActive = isActive || isQuoteFromAdmin || isTicketFromAdmin;
                        return `
                          flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                          ${shouldBeActive
                            ? 'bg-primary-500 text-white'
                            : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
                          }
                        `;
                      }}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              </>
            )}
          </nav>
          
          {/* User section */}
          <div className="p-4 border-t border-neutral-800">
            <NavLink
              to="/help"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-2
                ${isActive
                  ? 'bg-primary-500 text-white'
                  : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
                }
              `}
            >
              <LifeBuoy className="w-5 h-5" />
              Help
            </NavLink>
            <NavLink
              to="/profile"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-2
                ${isActive 
                  ? 'bg-primary-500 text-white' 
                  : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
                }
              `}
            >
              <User className="w-5 h-5" />
              Profile
            </NavLink>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-neutral-400 hover:bg-neutral-800 hover:text-white transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Sign out
            </button>
            {/* Off by a toggle in Portal Settings, for an MSP that white-labels fully. */}
            {branding.poweredBy && (
              <p className="px-3 pt-3 text-[11px] text-neutral-600">Powered by HaloMTS</p>
            )}
          </div>
        </div>
      </aside>
      
      {/* Main content */}
      <div className="lg:pl-64">
        {/* Above everything and never dismissable: a sandbox must not pass for production. */}
        <SandboxBanner />
        <PlatformBanners />
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            {/* Page title area (can be customized per page) */}
            <div className="hidden lg:block" />
            
            {/* Right side actions */}
            <div className="flex items-center gap-4">
              {/* Global Search - only show if documentation is enabled and user is internal */}
              {moduleFlags['module.documentation'] && isInternalStaff && (
                <NavLink
                  to="/documentation/search"
                  className="p-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
                  title="Global search"
                >
                  <Search className="w-5 h-5" />
                </NavLink>
              )}

              {/* Dark Mode Toggle - only show if feature flag enabled */}
              {darkModeEnabled && (
                <button
                  onClick={toggleDarkMode}
                  className="p-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
                  title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
              )}

              {/* Notifications */}
              <NotificationDropdown />
              
              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white font-medium text-sm">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-neutral-900 dark:text-white">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${roleBadge.className}`}>
                        {roleBadge.label}
                      </span>
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{user?.email}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-neutral-400 hidden sm:block" />
                </button>

                {/* Dropdown menu */}
                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-1 z-20">
                      <NavLink
                        to="/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                      >
                        <User className="w-4 h-4" />
                        Profile
                      </NavLink>
                      <hr className="my-1 border-neutral-200 dark:border-neutral-700" />
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          handleLogout();
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
